// The person's own account: their profile and their password. These pages are personal, they do not depend on
// any website or on the organisation, and they are apart from the settings of each website (the «Ajustes» tab
// of a website), which the note under the title says.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { Form } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { meQuery } from '../api';
import { runAuth } from '../auth-call';
import { authClient } from '../auth-client';
import { useAuthForm } from '../auth-form';
import { CooldownNotice, FormError, SubmitButton } from '../forms';
import { PASSWORD_LIMITS } from '../password-rules';
import { Field, Page, PasswordField } from '../ui';
import { readForm } from '../util';

/** The title, the note that says what these settings are, and the two pages of the account. */
function AccountShell({ title, children }: { title: string; children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <Page title={title}>
      <p className="mb-4 max-w-2xl text-ink-muted">{t('account.scope')}</p>
      <nav aria-label={t('account.nav')} className="account-tabs">
        <Link to="/account/profile">{t('account.menuProfile')}</Link>
        <Link to="/account/security">{t('account.menuSecurity')}</Link>
      </nav>
      <div className="max-w-xl">{children}</div>
    </Page>
  );
}

export function ProfilePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: me } = useQuery(meQuery);
  const form = useAuthForm();
  const [saved, setSaved] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    const name = (readForm(event).name ?? '').trim();
    setSaved(false);
    await form.run(async () => {
      const outcome = await runAuth((fetchOptions) => authClient.updateUser({ name, fetchOptions }));
      if (form.show(outcome)) return;
      await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
      // Said only now that the system has saved it.
      setSaved(true);
    });
  }

  return (
    <AccountShell title={t('account.profile.title')}>
      {me && (
        <Form onSubmit={submit} validationBehavior="native" className="flex flex-col gap-5">
          {form.problem && <FormError message={form.problem.message} attempt={form.problem.attempt} />}
          <CooldownNotice cooldown={form.cooldown} seconds={form.waitSeconds} />
          <Field
            name="name"
            label={t('account.profile.name')}
            defaultValue={me.user.name}
            autoComplete="name"
            maxLength={80}
            isRequired
            onChange={() => setSaved(false)}
          />
          <div className="flex flex-col gap-1">
            <span className="font-bold">{t('account.profile.email')}</span>
            <span className="wrap-anywhere">{me.user.email}</span>
            <span className="text-sm text-ink-muted">{t('account.profile.emailHint')}</span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <SubmitButton
              label={t('account.profile.save')}
              busyLabel={t('account.profile.saving')}
              busy={form.busy}
              waiting={form.cooldown.active}
            />
            <p role="status">{saved ? t('account.profile.saved') : ''}</p>
          </div>
        </Form>
      )}
    </AccountShell>
  );
}

export function SecurityPage() {
  const { t } = useTranslation();
  const form = useAuthForm();
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [newError, setNewError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    const formElement = event.currentTarget;
    const { currentPassword = '', newPassword = '' } = readForm(event);
    setDone(false);
    // A new answer is shown as new: the last one is taken away first.
    setCurrentError(null);
    setNewError(null);
    await form.run(async () => {
      // The other sessions are closed, as when the password is recovered; this one goes on.
      const outcome = await runAuth((fetchOptions) =>
        authClient.changePassword({ currentPassword, newPassword, revokeOtherSessions: true, fetchOptions }),
      );
      if (outcome.kind === 'credentials') {
        setCurrentError(t('account.security.currentWrong'));
        form.setProblem(null);
        return;
      }
      if (outcome.kind === 'password') {
        setNewError(t(`password.errors.${outcome.rule}`, PASSWORD_LIMITS));
        form.setProblem(null);
        return;
      }
      if (form.show(outcome)) return;
      formElement.reset();
      setDone(true);
    });
  }

  return (
    <AccountShell title={t('account.security.title')}>
      <Form onSubmit={submit} validationBehavior="native" className="flex flex-col gap-5">
        <p>{t('account.security.intro')}</p>
        {form.problem && <FormError message={form.problem.message} attempt={form.problem.attempt} />}
        <CooldownNotice cooldown={form.cooldown} seconds={form.waitSeconds} />
        <PasswordField
          name="currentPassword"
          label={t('account.security.current')}
          mode="current"
          error={currentError}
          isRequired
        />
        <PasswordField
          name="newPassword"
          label={t('account.security.new')}
          mode="new"
          error={newError}
          isRequired
        />
        <div className="flex flex-col gap-3">
          <SubmitButton
            label={t('account.security.submit')}
            busyLabel={t('account.security.submitting')}
            busy={form.busy}
            waiting={form.cooldown.active}
          />
          <p role="status" className="font-bold empty:hidden">
            {done ? t('account.security.done') : ''}
          </p>
        </div>
      </Form>
    </AccountShell>
  );
}
